// src/components/navSections.js
//
// Reorganized nav taxonomy — see specs/20260422-drawer-taxonomy/plan.md.
// Opt-in via localStorage `caro.portal.newNav === '1'`. When the flag is
// unset, Layout.js keeps rendering the original `menuItems` +
// `bottomMenuGroups` structure, so this can ship ahead of any broad
// rollout without disturbing existing users.
//
// The factory takes an auth/permission context so each item's
// visibility can be evaluated exactly as the old structure did.

import React from 'react';
import {
  Dashboard as DashboardIcon,
  LocalHospital as AlarmIcon,
  BugReport as TicketsIcon,
  Notifications as NotificationsIcon,
  Videocam as VideocamIcon,
  Engineering as EngineeringIcon,
  LocationOn as LocationsIcon,
  People as PeopleIcon,
  Security as UserManagementIcon,
  Cloud as DevicesIcon,
  Devices as DeviceTypeIcon,
  SystemUpdate as FirmwareIcon,
  Link as LinkIcon,
  RadioButtonChecked as ButtonIcon,
  SmartToy as AutomationIcon,
  PowerSettingsNew as PowerIcon,
  AutoFixHigh as BulkNameIcon,
  Phone as PhoneIcon,
  Router as RouterIcon,
  Map as MapIcon,
  TabletMac as EinkIcon,
  IntegrationInstructions as IntegrationIcon,
  Business as BusinessIcon,
  NotificationsActive as NotificationsActiveIcon,
  Category as CategoryIcon,
  Apps as AppsIcon,
  Monitor as MonitorIcon,
  Code as CodeIcon,
  Help as HelpIcon,
  MarkEmailUnread as QuarantineIcon,
  Visibility as MonitorSectionIcon,
  FolderShared as DirectoryIcon,
  Memory as HardwareIcon,
  Hub as AutomationSectionIcon,
  CorporateFare as OrganizationIcon,
  Settings as SystemSectionIcon,
} from '@mui/icons-material';

/**
 * Build the taxonomy. Returns an array of sections; each section has
 * `items: [{ text, path, icon, adminOnly?, requiresPermission? }]`.
 *
 * The shape matches what Layout's existing rendering logic expects for
 * groups — no renderer changes required.
 */
export function buildNewNavSections({ t, cameraRequestsPermission, fieldInterventionsPermission, canManageUsers, companyManagementPermission }) {
  const tr = (key, fallback) => (t ? t(key) : fallback);

  return [
    {
      id: 'monitor',
      label: tr('Nav.monitor', 'Monitor'),
      icon: <MonitorSectionIcon />,
      defaultOpen: true,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_dashboard', 'Dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
        { text: tr('Layout.menu_alarms', 'Alarms'), icon: <AlarmIcon />, path: '/alarms', adminOnly: true },
        { text: tr('Layout.menu_tickets', 'Tickets'), icon: <TicketsIcon />, path: '/tickets' },
        { text: tr('Layout.menu_ticket_quarantine', 'E-mail Quarantine'), icon: <QuarantineIcon />, path: '/tickets/quarantine' },
        { text: tr('Layout.menu_notifications', 'Notifications'), icon: <NotificationsIcon />, path: '/notifications' },
        { text: tr('Layout.menu_camera_requests', 'Camera Requests'), icon: <VideocamIcon />, path: '/camera-requests', requiresPermission: cameraRequestsPermission },
        { text: tr('Layout.menu_field_interventions', 'Field Interventions'), icon: <EngineeringIcon />, path: '/field-interventions', requiresPermission: fieldInterventionsPermission },
      ],
    },
    {
      id: 'directory',
      label: tr('Nav.directory', 'Directory'),
      icon: <DirectoryIcon />,
      defaultOpen: true,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_locations', 'Locations'), icon: <LocationsIcon />, path: '/locations' },
        { text: tr('Layout.menu_persons', 'Persons'), icon: <PeopleIcon />, path: '/persons' },
        { text: tr('Layout.menu_user_management', 'User management'), icon: <UserManagementIcon />, path: '/users', requiresPermission: canManageUsers },
      ],
    },
    {
      id: 'hardware',
      label: tr('Nav.hardware', 'Hardware'),
      icon: <HardwareIcon />,
      defaultOpen: false,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_devices', 'Devices'), icon: <DevicesIcon />, path: '/devices' },
        { text: tr('Layout.menu_tablet_permissions', 'Tablet Permissions'), icon: <DeviceTypeIcon />, path: '/tablet-permissions', adminOnly: true },
        { text: tr('Layout.menu_device_type_management', 'Device Type Management'), icon: <DeviceTypeIcon />, path: '/device-type-management', adminOnly: true },
        { text: tr('Layout.menu_device_linking', 'Device Linking'), icon: <LinkIcon />, path: '/device-linking', adminOnly: true },
        { text: tr('Layout.menu_firmware_management', 'Firmware Management'), icon: <FirmwareIcon />, path: '/firmware-management', adminOnly: true },
        { text: tr('Layout.menu_enocean_buttons', 'EnOcean Buttons'), icon: <ButtonIcon />, path: '/enocean-buttons', adminOnly: true },
        { text: tr('Layout.menu_enocean_import', 'EnOcean Import'), icon: <ButtonIcon />, path: '/enocean-import', adminOnly: true },
      ],
    },
    {
      id: 'automation',
      label: tr('Nav.automation', 'Automation & Integrations'),
      icon: <AutomationSectionIcon />,
      defaultOpen: false,
      adminOnly: true,
      items: [
        { text: tr('Layout.menu_automations', 'Automations'), icon: <AutomationIcon />, path: '/automations', adminOnly: true },
        { text: tr('Layout.menu_qbus_output_types', 'Qbus Output Types'), icon: <PowerIcon />, path: '/qbus-output-types', adminOnly: true },
        { text: tr('Layout.menu_qbus_friendly_names', 'Qbus Friendly Names'), icon: <BulkNameIcon />, path: '/qbus-friendly-names', adminOnly: true },
        { text: tr('Layout.menu_qbus_monitor', 'Qbus Monitor'), icon: <MonitorIcon />, path: '/qbus-monitor', adminOnly: true },
        { text: tr('Layout.menu_telephony', 'Telephony'), icon: <PhoneIcon />, path: '/telephony', adminOnly: true },
        { text: tr('Layout.menu_baseip_management', 'BaseIP Management'), icon: <RouterIcon />, path: '/baseip-management', adminOnly: true },
        { text: tr('Layout.menu_indoor_mapping', 'Indoor Mapping'), icon: <MapIcon />, path: '/indoor-mapping', adminOnly: true },
        { text: tr('Layout.menu_eink_designer', 'E-Ink Designer'), icon: <EinkIcon />, path: '/eink-designer', adminOnly: true },
        { text: tr('Layout.menu_integration_management', 'Integration Management'), icon: <IntegrationIcon />, path: '/integration-management', adminOnly: true },
        { text: tr('Layout.menu_location_data_import', 'Location Data Import'), icon: <LocationsIcon />, path: '/location-data-import', adminOnly: true },
        { text: tr('Layout.menu_unit_audit', 'Unit Audit'), icon: <LocationsIcon />, path: '/unit-audit', adminOnly: true },
      ],
    },
    {
      id: 'organization',
      label: tr('Nav.organization', 'Organization'),
      icon: <OrganizationIcon />,
      defaultOpen: false,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_send_notifications', 'Send Notifications'), icon: <NotificationsActiveIcon />, path: '/notification-management', requiresPermission: canManageUsers },
        { text: tr('Layout.menu_company_management', 'Company Management'), icon: <BusinessIcon />, path: '/company-management', requiresPermission: companyManagementPermission },
        { text: tr('Layout.menu_person_type_management', 'Person Type Management'), icon: <CategoryIcon />, path: '/person-type-management', requiresPermission: canManageUsers },
      ],
    },
    {
      id: 'system',
      label: tr('Nav.system', 'System'),
      icon: <SystemSectionIcon />,
      defaultOpen: false,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_service_management', 'Service Management'), icon: <AppsIcon />, path: '/service-management', adminOnly: true },
        { text: tr('Layout.menu_monitoring', 'Monitoring'), icon: <MonitorIcon />, path: '/monitoring', adminOnly: true },
        { text: tr('Layout.menu_hub_status', 'Hub Status'), icon: <MonitorIcon />, path: '/hub-status', adminOnly: true },
        { text: tr('Layout.menu_hub_location_status', 'Hub Location Status'), icon: <MonitorIcon />, path: '/hub-location-status', adminOnly: true },
        { text: tr('Layout.menu_developer_info', 'Developer Info'), icon: <CodeIcon />, path: '/developer-info', adminOnly: true },
        { text: tr('Layout.menu_help', 'Help'), icon: <HelpIcon />, path: '/help' },
      ],
    },
  ];
}

// Map old `menuOpenGroups` keys to new section ids so users who had
// collapsed/expanded particular groups don't lose their state.
export const OLD_TO_NEW_GROUP_KEY = {
  'management': 'organization',
  'device-management': 'hardware',
  'integrations': 'automation',
  'system': 'system',
};

export const NAV_FLAG_KEY = 'caro.portal.newNav';
